import axios from "axios"
import { normalizeInfluencer } from "./normalizeInfluencer"
import { okara_api } from "../../utils/variables"

// Same UGC/socials-intelligence backend as topInfluencerApi.js /
// influencerProfileApi.js.
const UGC_API_BASE_URL = okara_api
const FAVOURITES_ENDPOINT = "/top-influencer/favourites/"

const authHeaders = () => {
  const token = localStorage.getItem("authToken")
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// Same "narrow, never reorder" filter param map as topInfluencerApi.js -
// INFLUENCER_FAVOURITES_API.md §4 says the saved list takes every filter and
// sort the ranking does, unchanged.
const FILTER_PARAM_MAP = {
  creatorSize: "creator_size",
  minFollowers: "min_followers",
  maxFollowers: "max_followers",
  minPosts: "min_posts",
  maxPosts: "max_posts",
  minTotalViews: "min_total_views",
  maxTotalViews: "max_total_views",
  minPostsAboutApps: "min_posts_about_apps",
  appId: "app_id",
  appIds: "app_ids",
  verifiedOnly: "verified_only",
  hasEmail: "has_email",
  hasLinks: "has_links",
  hasDescription: "has_description",
  excludePrivate: "exclude_private",
  mirroredImageOnly: "mirrored_image_only",
  discoveredWithinDays: "discovered_within_days",
  refreshedWithinDays: "refreshed_within_days",
  staleOnly: "stale_only",
}

const asApiError = (error) => {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  const err = new Error(data.error || error.message || "Failed to load your favourite influencers.")
  err.status = status
  err.data = data
  return err
}

// §4 - `favourites`/`total_favourites`/`total_favourites_unfiltered`, plus
// `missing`/`missing_entries` for saves whose influencer has since left the
// store. Rows get the same `key` normalizeInfluencer adds to a ranking row.
const normalizeFavouritesResponse = (raw) => ({
  ...raw,
  favourites: (raw.favourites || []).map(normalizeInfluencer),
})

/**
 * §4 - the saved list, GET. Mirrors fetchTopInfluencers's shape exactly
 * (topInfluencerApi.js) since it's the same filter/sort/paging contract
 * against a different base path and a different default sort (`saved`
 * here vs `audience` there).
 */
export async function fetchInfluencerFavourites({
  page = 1,
  pageSize = 20,
  sort = "saved",
  includeFacets = false,
  includeBreakdown = false,
  filters = {},
} = {}) {
  const params = { page, page_size: pageSize, sort }
  if (includeFacets) params.include_facets = true
  if (includeBreakdown) params.include_breakdown = true

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === false) return
    if (Array.isArray(value) && value.length === 0) return
    const paramKey = FILTER_PARAM_MAP[key] || key
    params[paramKey] = Array.isArray(value) ? value.join(",") : value
  })

  try {
    const response = await axios.get(`${UGC_API_BASE_URL}${FAVOURITES_ENDPOINT}`, {
      params,
      headers: authHeaders(),
    })
    return normalizeFavouritesResponse(response.data)
  } catch (error) {
    throw asApiError(error)
  }
}

// Shared by save/remove - §2/§3 accept `profile_url`, or `platform` +
// `username`, or `platform` + `platform_key` (the one form that still works
// once an influencer has left the store - §7). Sent as query params on both
// methods per the docs ("DELETE with a body is not universally supported by
// clients").
const identityParams = ({ profileUrl, platform, username, platformKey }) => {
  const params = {}
  if (profileUrl) params.profile_url = profileUrl
  if (platform) params.platform = platform
  if (username) params.username = username
  if (platformKey) params.platform_key = platformKey
  return params
}

/**
 * §2 - save one. Idempotent: saving an already-saved influencer is a
 * success with `created: false`, not an error.
 */
export async function saveInfluencerFavourite(identity) {
  try {
    const response = await axios.post(
      `${UGC_API_BASE_URL}${FAVOURITES_ENDPOINT}`,
      {},
      { params: identityParams(identity), headers: authHeaders() }
    )
    return response.data
  } catch (error) {
    throw asApiError(error)
  }
}

/**
 * §3 - unsave one. Idempotent and always 200 when the identifiers resolve
 * (`removed: false` just means it was never saved) - the one write that
 * deliberately does not check the influencer store, so it also clears a
 * favourite whose influencer has since left it (pass `platformKey` for that
 * case - §7).
 */
export async function removeInfluencerFavourite(identity) {
  try {
    const response = await axios.delete(`${UGC_API_BASE_URL}${FAVOURITES_ENDPOINT}`, {
      params: identityParams(identity),
      headers: authHeaders(),
    })
    return response.data
  } catch (error) {
    throw asApiError(error)
  }
}
